import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { CurrencyProvider } from "./context/CurrencyContext";
import { WishlistProvider } from "./context/WishlistContext";
import { I18nProvider } from "./context/I18nContext";
import AuthCallback from "./pages/AuthCallback";
import Navbar from "./components/Navbar";
import CartDrawer from "./components/CartDrawer";
import Footer from "./components/Footer";
import Preloader from "./components/Preloader";
import SeoManager from "./components/SeoManager";
import PageTransition from "./components/PageTransition";
import CartReminder from "./components/CartReminder";
import Home from "./pages/Home";

const Shop = lazy(() => import("./pages/Shop"));
const Product = lazy(() => import("./pages/Product"));
const Checkout = lazy(() => import("./pages/Checkout"));
const PaymentReturn = lazy(() => import("./pages/PaymentReturn"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const Track = lazy(() => import("./pages/Track"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const Account = lazy(() => import("./pages/Account"));
const Legal = lazy(() => import("./pages/Legal"));
const Admin = lazy(() => import("./pages/Admin"));
const About = lazy(() => import("./pages/About"));
const Lookbook = lazy(() => import("./pages/Lookbook"));
const GiftReceipt = lazy(() => import("./pages/GiftReceipt"));

const PAGES = [
  ["/", Home],
  ["/shop", Shop],
  ["/product/:id", Product],
  ["/checkout", Checkout],
  ["/payment/return", PaymentReturn],
  ["/wishlist", Wishlist],
  ["/track", Track],
  ["/auth", AuthPage],
  ["/account", Account],
  ["/legal/:slug", Legal],
  ["/admin", Admin],
  ["/about", About],
  ["/lookbook", Lookbook],
  ["/gift/:orderId", GiftReceipt],
];

function AnimatedRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        {PAGES.map(([path, Page]) => (
          <Route
            key={path}
            path={path}
            element={
              <PageTransition>
                <Suspense fallback={<div className="min-h-screen" />}>
                  <Page />
                </Suspense>
              </PageTransition>
            }
          />
        ))}
      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  const [ready, setReady] = useState(false);
  const reveal = useCallback(() => setReady(true), []);

  useEffect(() => {
    const lenis = new Lenis({ duration: 0.9, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <I18nProvider>
    <AuthProvider>
      <CurrencyProvider>
        <CartProvider>
        <WishlistProvider>
          <BrowserRouter>
            <Preloader onReveal={reveal} />
            {ready && (
              <div className="min-h-screen bg-ivory text-ink font-sans">
                <SeoManager />
                <Navbar />
                <AnimatedRoutes />
                <Footer />
                <CartDrawer />
                <CartReminder />
              </div>
            )}
            <Toaster
              theme="light"
              position="bottom-right"
              toastOptions={{
                style: {
                  background: "#FAF7F1",
                  border: "1px solid rgba(185,150,43,0.45)",
                  color: "#0E0D0B",
                  borderRadius: "2px",
                },
              }}
            />
          </BrowserRouter>
        </WishlistProvider>
        </CartProvider>
      </CurrencyProvider>
    </AuthProvider>
    </I18nProvider>
  );
}
